/**
 * Centralized Axios API Client
 *
 * Provides a configured singleton Axios instance with:
 * - Base URL from centralized api.config.js
 * - Request interceptor: Automatically attaches JWT bearer tokens from localStorage
 * - Response interceptor: Normalizes errors and handles 401/auth expiration gracefully
 * - Standard convenience HTTP methods (get, post, put, patch, del)
 */

import axios from 'axios';
import { API_BASE_URL } from '../configs/api.config';

// 1. Create singleton Axios instance
const apiClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
    Accept: 'application/json',
  },
});

// Helper for programmatic token override (in addition to localStorage)
let activeTokenOverride = null;

export const setClientAuthToken = (token) => {
  activeTokenOverride = token || null;
  if (token) {
    apiClient.defaults.headers.common.Authorization = `Bearer ${token}`;
  } else {
    delete apiClient.defaults.headers.common.Authorization;
  }
};

// 2. Request Interceptor: Auto-attach JWT token
apiClient.interceptors.request.use(
  (config) => {
    // If an Authorization header is already explicitly provided in the request config, keep it
    if (!config.headers.Authorization) {
      const token =
        activeTokenOverride ||
        (typeof window !== 'undefined'
          ? localStorage.getItem('token') || localStorage.getItem('authToken')
          : null);

      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// 3. Response Interceptor: Standardize errors and handle session expiry
apiClient.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    // Normalized error message extraction
    const serverMessage =
      error.response?.data?.message ||
      error.response?.data?.error ||
      error.response?.data?.msg ||
      error.message ||
      'An unexpected network error occurred';

    // Enhance error object with readable message
    if (error.response?.data && typeof error.response.data === 'object') {
      error.response.data.normalizedMessage = serverMessage;
    }

    // Handle 401 Unauthorized globally if token is stale
    if (error.response?.status === 401) {
      console.warn('[apiClient] 401 Unauthorized response received:', serverMessage);
    }

    return Promise.reject(error);
  }
);

// 4. Convenience HTTP method helpers
export const http = {
  get: (url, config = {}) => apiClient.get(url, config),
  post: (url, data = {}, config = {}) => apiClient.post(url, data, config),
  put: (url, data = {}, config = {}) => apiClient.put(url, data, config),
  patch: (url, data = {}, config = {}) => apiClient.patch(url, data, config),
  del: (url, config = {}) => apiClient.delete(url, config),
};

export { apiClient };
export default apiClient;
